import { red } from '@mui/material/colors';
import React, { useContext } from 'react';
import { get_cod } from '../../../utils/cod';
import {
  displayLocation,
  extractMapsPlaceLink,
} from '../../../utils/locations';
import { add, displayMoney } from '../../../utils/money';

import CopyLinkButton from '../../common/copy-link-button/copy-link-button';

import { GenericList } from '../../common/generic-list/generic-list';
import {
  IconWithText,
  mobileDoubleItemsGrid,
} from '../../common/styles/components';
import { showDeliveryBatchChip } from '../../common/styles/delivery-batch.styles';
import { showDeliveryState } from '../../common/styles/order-states.style';
import {
  getPickupTaskStateColor,
  getPickupTaskStateIcon,
  showPickupTaskChip,
  showPickupTaskState,
} from '../../common/styles/pickup-task.styles';
import { showWorkflow } from '../../common/styles/workflow-display';
import { Updater } from 'use-immer';
import { SortingByColumnState } from '../../common/generic-table/utils';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LinkIcon from '@mui/icons-material/Link';
import { IPickupTask } from '../../../interfaces/delivery-company/tasks';
import { format } from 'date-fns';
import { Box, CircularProgress, IconButton } from '@mui/material';
import { AuthContext } from '../../../contexts/user-context/user-context';
import HouseIcon from '@mui/icons-material/House';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DownloadIcon from '@mui/icons-material/Download';
import { isDesktop, isMobile, isTablet } from 'react-device-detect';
import CancelIcon from '@mui/icons-material/Cancel';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { DriverWithIcon } from '../../common/styles/driver-with-icon';
import { FactCheckOutlined } from '@mui/icons-material';
import {
  DriverInvoiceState,
  DriverInvoiceType,
  IDriverInvoice,
} from '../../../interfaces/common/driver-invoices';
import { showDriverInvoiceState } from '../../../utils/driver-invoices';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import { ClearIcon } from '@mui/x-date-pickers';
import VerifiedIcon from '@mui/icons-material/Verified';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentIcon from '@mui/icons-material/Payment';
import NotesIcon from '@mui/icons-material/Notes';
import {
  getDriverInvoiceStateColor,
  getDriverInvoiceStateIcon,
  showDriverInvoiceType,
} from '../../common/styles/driver-invoice';

interface ResponsiveDriverInvoicesListingProps {
  invoicesList: IDriverInvoice[];
  isLoading: boolean;
  setLockedInvoiceMeta: React.Dispatch<
    React.SetStateAction<IDriverInvoice | null>
  >;
  setLockType: React.Dispatch<React.SetStateAction<'view' | 'delete' | null>>;
  fetchInvoiceContent: (invoicemeta: IDriverInvoice) => Promise<void>;
  lockedInvoiceMeta: IDriverInvoice | null;
  lockType: 'view' | 'delete' | null;
}

const ResponsiveInvoicesListing = ({
  lockType,
  lockedInvoiceMeta,
  fetchInvoiceContent,
  invoicesList,
  isLoading,
  setLockedInvoiceMeta,
  setLockType,
}: ResponsiveDriverInvoicesListingProps) => {
  const { user, config } = useContext(AuthContext);
  return (
    <GenericList<IDriverInvoice>
      sourceView="driver-invoices-listing"
      items={invoicesList}
      selectedItems={new Map()}
      updateSelectedItems={() => {
        throw new Error('unimplemented');
      }}
      isLoading={isLoading}
      fullWidth
      columns={[
        {
          id: 'id',
          header: <b>Invoice #</b>,
          display_body: (invoice) => <b>{invoice.id}</b>,
        },
        {
          id: 'for',
          header: <b>For</b>,
          display_body: (invoice) => (
            <p>
              <DeliveryDiningIcon color="primary" /> <br />{' '}
              {invoice.driver?.name} <br />
              {invoice.driver?.phone_number}
            </p>
          ),
        },
        {
          id: 'type',
          header: <b>Type</b>,
          display_body: (invoice) => showDriverInvoiceType(invoice.type),
        },
        {
          id: 'package_count',
          header: <b>Items</b>,
          display_body: (invoice) => (
            <p>
              {invoice.type === DriverInvoiceType.PICKUP ? (
                <>{invoice.task_count} pickups</>
              ) : (
                <>{invoice.packages} packages</>
              )}
            </p>
          ),
        },
        {
          id: 'commission',
          header: <b>Commission</b>,
          display_body: (invoice) => (
            <>
              {add(
                invoice.total_amounts.driver_commission,
                invoice.total_amounts.pop
              )}
            </>
          ),
        },
        {
          id: 'due_amount',
          header: <b>Due Amount</b>,
          display_body: (invoice) => (
            <>{displayMoney(invoice.total_amounts.due_to_company)}</>
          ),
        },
        {
          id: 'created_at',
          header: <b>Created At</b>,
          display_body: (invoice) => (
            <>{new Date(invoice.created_at).toLocaleString()}</>
          ),
        },
        {
          id: 'payment_state',
          header: <b>Payment State</b>,
          display_body: (invoice) => {
            return showDriverInvoiceState(invoice.status);
          },
        },
        {
          id: 'notes',
          header: <b>Notes</b>,
          display_body: (invoice) => <> {invoice.notes} </>,
        },
        {
          id: 'view_invoice',
          header: <b>View</b>,
          display_body: (invoice) => (
            <>
              {' '}
              <IconButton
                onClick={() => fetchInvoiceContent(invoice)}
                disabled={lockedInvoiceMeta !== null}
              >
                {lockedInvoiceMeta?.id === invoice.id && lockType === 'view' ? (
                  <CircularProgress />
                ) : (
                  <PreviewIcon fontSize="large" />
                )}
              </IconButton>
            </>
          ),
        },
      ]}
      getItemUID={(order) => order.id}
      rowConfigs={[
        {
          display_row: (invoice) => <b>Invoice #{invoice.id}</b>,
        },
        {
          display_row: (invoice) => (
            <DriverWithIcon centered driver={invoice.driver} />
          ),
        },
        {
          display_row: (invoice) => (
            <IconWithText centered>
              {new Date(invoice.created_at).toLocaleString()}
            </IconWithText>
          ),
        },
        {
          display_row: (invoice) =>
            mobileDoubleItemsGrid([
              showDriverInvoiceType(invoice.type),
              <Box
                key={'items'}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '12px',
                  padding: '4px 8px',
                }}
              >
                {invoice.type === DriverInvoiceType.PICKUP
                  ? `${invoice.task_count} pickups`
                  : `${invoice.packages} packages`}
              </Box>,
            ]),
        },
        {
          display_row: (invoice) =>
            mobileDoubleItemsGrid([
              <div
                key={'amount'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <b>{'Total Commission'}</b>{' '}
                {displayMoney(invoice.total_amounts.driver_commission)}
              </div>,
              <div
                key={'amount'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <b>{'Total Due'}</b>{' '}
                {displayMoney(invoice.total_amounts.due_to_company)}
              </div>,
            ]),
        },
        {
          display_row: (invoice) =>
            mobileDoubleItemsGrid([
              <div
                key={'invoice notes'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <b>Notes</b>
                <>{invoice.notes}</>
              </div>,
            ]),
        },
      ]}
      cardFooter={(invoice) => {
        return {
          content: (
            <IconWithText centered>
              {getDriverInvoiceStateIcon(invoice.status)}
              {invoice.status.replaceAll('_', ' ')}
            </IconWithText>
          ),
          background: getDriverInvoiceStateColor(invoice.status),
        };
      }}
      cardIcon={(invoice) => <MonetizationOnIcon color="primary" />}
      onCardClick={(invoice) => fetchInvoiceContent(invoice)}
    />
  );
};

export default ResponsiveInvoicesListing;
